import React, { useState } from 'react';
import { useAuth } from './AuthContext';
import api from '../services/api';
import ElementalLogo from '../components/ElementalLogo';
import { 
  Mail, Lock, Eye, EyeOff, ArrowRight, Loader, User,
  AlertCircle, CheckCircle, TrendingUp, Check, Crown, Building2, Zap, ChevronDown
} from 'lucide-react';
import './Auth.css';

const { Payments } = api;

const PLANS = [
  { key: 'free', name: 'Free', price: '$0', sub: 'forever', color: '#10b981', icon: Zap, tag: null,
    summary: 'All public data + paper trading',
    details: ['Every Congress trade & SEC filing', 'Lobbying & gov contract data', 'Paper trading portfolio', '5 AI analyses per day', '15-min delayed prices'] },
  { key: 'plus', name: 'Plus', price: '$29', sub: '/mo', color: '#667eea', icon: TrendingUp, tag: null,
    summary: 'Real-time data + screeners + alerts',
    details: ['Everything in Free', 'Real-time market prices', 'Advanced filters on all datasets', 'Stock screener', '10 trade alerts/day', '10 AI analyses/day'] },
  { key: 'pro', name: 'Pro', price: '$49', sub: '/mo', color: '#f59e0b', icon: Crown, tag: 'Most Popular',
    summary: 'Full AI + convergence signals',
    details: ['Everything in Plus', 'Unlimited AI chat assistant', 'Convergence signal detection', 'Insider cluster buy alerts', 'Lobbying \u2192 stock correlation', '50 AI trade alerts/day'] },
  { key: 'enterprise', name: 'Enterprise', price: '$149', sub: '/mo', color: '#8b5cf6', icon: Building2, tag: null,
    summary: 'API, exports & backtesting',
    details: ['Everything in Pro', 'Full REST API access', 'CSV/JSON bulk exports', 'Portfolio backtesting', 'Unlimited trade alerts', 'Priority support'] },
];

const SignupPage = ({ onNavigate, onSuccess }) => {
  const { signup, loading, error } = useAuth();
  
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [expandedPlan, setExpandedPlan] = useState(null);

  const getPasswordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: '' };
    let score = 0;
    if (pwd.length >= 8) score++;
    if (pwd.length >= 12) score++;
    if (/[a-z]/.test(pwd) && /[A-Z]/.test(pwd)) score++;
    if (/\d/.test(pwd)) score++;
    if (/[^a-zA-Z0-9]/.test(pwd)) score++;
    if (score <= 1) return { score: 1, label: 'Weak', color: '#ef4444' };
    if (score <= 2) return { score: 2, label: 'Fair', color: '#f59e0b' };
    if (score <= 3) return { score: 3, label: 'Good', color: '#10b981' };
    return { score: 4, label: 'Strong', color: '#22c55e' };
  };

  const passwordStrength = getPasswordStrength(password);

  const validateForm = () => {
    if (!name.trim()) { setFormError('Name is required'); return false; }
    if (!email) { setFormError('Email is required'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setFormError('Please enter a valid email'); return false; }
    if (!password) { setFormError('Password is required'); return false; }
    if (password.length < 8) { setFormError('Password must be at least 8 characters'); return false; }
    if (password !== confirmPassword) { setFormError('Passwords do not match'); return false; }
    if (!acceptTerms) { setFormError('Please accept the terms and conditions'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const result = await signup(email, password, name);

      if (result.success) {
        if (selectedPlan !== 'free') {
          try {
            await Payments.createCheckout(selectedPlan);
          } catch (stripeErr) {
            console.error('Stripe checkout error:', stripeErr);
            if (onSuccess) onSuccess();
          }
        } else {
          if (onSuccess) onSuccess();
        }
      } else {
        setFormError(result.error || 'Registration failed. Please try again.');
      }
    } catch (err) {
      setFormError(err.message || 'Registration failed. Please try again.');
    }

    setIsSubmitting(false);
  };

  const toggleExpand = (key, e) => {
    e.stopPropagation();
    setExpandedPlan(expandedPlan === key ? null : key);
  };

  const getButtonText = () => {
    if (isSubmitting && selectedPlan !== 'free') return 'Creating account & redirecting...';
    if (isSubmitting) return 'Creating account...';
    if (selectedPlan !== 'free') {
      const plan = PLANS.find(p => p.key === selectedPlan);
      return `Create Account & Get ${plan.name}`;
    }
    return 'Create Account';
  };

  return (
    <div className="auth-page">
      <div className="auth-bg">
        <div className="auth-bg-gradient" />
        <div className="auth-bg-grid" />
        <div className="auth-bg-glow auth-bg-glow-1" />
        <div className="auth-bg-glow auth-bg-glow-2" />
      </div>

      <div className="auth-container auth-container-wide">
        <div className="auth-branding">
          <div className="auth-brand-content">
            <div className="auth-logo">
              <ElementalLogo variant="full" />
            </div>
            
            <h1 className="auth-tagline">
              Every Market.
              <span className="auth-tagline-highlight">Every Signal. One Platform.</span>
            </h1>
            
            <p className="auth-description" style={{ marginBottom: '20px' }}>
              Stocks, crypto, macro, politics, insider moves, institutional flow — 
              all the data you need with AI that connects the dots.
            </p>

            <div className="signup-plans">
              {PLANS.map((plan) => {
                const Icon = plan.icon;
                const isSelected = selectedPlan === plan.key;
                const isExpanded = expandedPlan === plan.key;
                return (
                  <div key={plan.key} className="signup-plan-wrapper">
                    <button
                      className={`signup-plan ${isSelected ? 'active' : ''}`}
                      onClick={() => setSelectedPlan(plan.key)}
                      style={isSelected ? { borderColor: plan.color, boxShadow: `0 0 16px ${plan.color}12` } : {}}
                    >
                      {plan.tag && <span className="signup-plan-tag" style={{ background: plan.color }}>{plan.tag}</span>}
                      
                      <div className="signup-plan-row">
                        <div className="signup-plan-left">
                          {isSelected ? (
                            <CheckCircle size={16} style={{ color: plan.color, flexShrink: 0 }} />
                          ) : (
                            <div className="signup-plan-radio" />
                          )}
                          <Icon size={14} style={{ color: plan.color, flexShrink: 0 }} />
                          <span className="signup-plan-name">{plan.name}</span>
                          <span className="signup-plan-summary">{plan.summary}</span>
                        </div>
                        <div className="signup-plan-right">
                          <span className="signup-plan-price" style={{ color: plan.color }}>{plan.price}</span>
                          <span className="signup-plan-period">{plan.sub}</span>
                          <button 
                            className={`signup-plan-info ${isExpanded ? 'expanded' : ''}`}
                            onClick={(e) => toggleExpand(plan.key, e)}
                            title="View details"
                          >
                            <ChevronDown size={14} />
                          </button>
                        </div>
                      </div>
                    </button>

                    <div className={`signup-plan-details ${isExpanded ? 'open' : ''}`}>
                      <ul>
                        {plan.details.map((d, i) => (
                          <li key={i}>
                            <Check size={11} style={{ color: plan.color, flexShrink: 0 }} />
                            <span>{d}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedPlan !== 'free' && (
              <p className="signup-plan-note">You'll be redirected to secure checkout after creating your account.</p>
            )}
          </div>
        </div>

        <div className="auth-form-panel">
          <div className="auth-form-container">
            <div className="auth-form-header">
              <h2>Create your account</h2>
              <p>Get started with ELEMENTAL today</p>
            </div>

            {(formError || error) && (
              <div className="auth-alert auth-alert-error">
                <AlertCircle size={18} />
                <span>{formError || error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label htmlFor="name">Full Name</label>
                <div className="auth-input-wrapper">
                  <User size={18} className="auth-input-icon" />
                  <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="John Doe" autoComplete="name" disabled={isSubmitting} />
                </div>
              </div>

              <div className="auth-input-group">
                <label htmlFor="email">Email</label>
                <div className="auth-input-wrapper">
                  <Mail size={18} className="auth-input-icon" />
                  <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" disabled={isSubmitting} />
                </div>
              </div>

              <div className="auth-input-group">
                <label htmlFor="password">Password</label>
                <div className="auth-input-wrapper">
                  <Lock size={18} className="auth-input-icon" />
                  <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" disabled={isSubmitting} />
                  <button type="button" className="auth-input-toggle" onClick={() => setShowPassword(!showPassword)} tabIndex={-1}>
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                {password && (
                  <div className="auth-password-strength">
                    <div className="auth-strength-bar">
                      <div className="auth-strength-fill" style={{ width: `${(passwordStrength.score / 4) * 100}%`, backgroundColor: passwordStrength.color }} />
                    </div>
                    <span style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
                  </div>
                )}
              </div>

              <div className="auth-input-group">
                <label htmlFor="confirmPassword">Confirm Password</label>
                <div className="auth-input-wrapper">
                  <Lock size={18} className="auth-input-icon" />
                  <input id="confirmPassword" type={showPassword ? 'text' : 'password'} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder="••••••••" autoComplete="new-password" disabled={isSubmitting} />
                  {confirmPassword && password === confirmPassword && <CheckCircle size={18} className="auth-input-valid" />}
                </div>
              </div>

              <label className="auth-checkbox auth-terms">
                <input type="checkbox" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} disabled={isSubmitting} />
                <span className="auth-checkbox-mark" />
                <span>
                  I agree to the{' '}
                  <button type="button" className="auth-link-inline">Terms of Service</button>
                  {' '}and{' '}
                  <button type="button" className="auth-link-inline">Privacy Policy</button>
                </span>
              </label>

              <button type="submit" className="auth-btn auth-btn-primary" disabled={isSubmitting}>
                {isSubmitting ? (
                  <><Loader size={18} className="auth-spinner" /><span>{getButtonText()}</span></>
                ) : (
                  <><span>{getButtonText()}</span><ArrowRight size={18} /></>
                )}
              </button>
            </form>

            <div className="auth-footer">
              <span>Already have an account?</span>
              <button type="button" className="auth-link" onClick={() => onNavigate && onNavigate('login')}>Sign in</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;